import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, CheckCircle, Droplet, Home, Building, Wind, Zap, Star, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react'; // "Calendar," 
import { FaYelp, FaReddit, FaTiktok, FaYoutube, FaDiscord } from 'react-icons/fa6';
import LegalPage from './Legal';
import { mountInlineEmbed, mountFloatingButton } from './cal';

const WORKER_URL = 'https://super-hill-1c48.officermills641.workers.dev';


// =============================================
// 🛑 SYSTEM TECHNICIANS ONLY | DO NOT TOUCH 🛑
// =============================================
// MAINTENANCE MODE TOGGLE
// 🔴 "true"  = Public RESTRICTED
// 🟢 "false" = Public ALLOWED
const MAINTENANCE_MODE = true; //===> 🟢 <===//
const ADMIN_SECRET = 'aw1540adminjs1988';
//          **/?admin=aw1540adminjs1988
// =============================================
// 🛑 SYSTEM TECHNICIANS ONLY | DO NOT TOUCH 🛑
// =============================================


// ============================================
// MAINTENANCE PAGE COMPONENT
// ============================================
const MaintenancePage = () => {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated caution tape - top */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-yellow-400 border-y-4 border-black transform -rotate-3 origin-left animate-pulse">
        <div className="flex items-center justify-around h-full text-black font-bold text-lg">
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
        </div>
      </div>

      {/* Animated caution tape - bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-yellow-400 border-y-4 border-black transform rotate-2 origin-right animate-pulse">
        <div className="flex items-center justify-around h-full text-black font-bold text-lg">
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
          <span>⚠️ CAUTION ⚠️</span>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full text-center z-10 border-4 border-yellow-400">
        <div className="mb-6">
          <div className="inline-block bg-yellow-400 p-4 rounded-full mb-4">
            <svg className="w-16 h-16 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            A&W Pressure Washing
          </h1>
          <div className="h-1 w-32 bg-blue-500 mx-auto mb-6"></div>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Site Under Maintenance
          </h2>
          <p className="text-gray-600 text-lg">
            We're currently performing scheduled maintenance to improve your experience.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
            <p className="text-gray-700 font-medium">
              ⏱️ Estimated time: <span className="text-orange-600 font-bold">1 hour</span>
            </p>
          </div>
          <p className="text-gray-500">
            Thank you for your patience. We'll be back shortly!
          </p>
        </div>

        <div className="mt-8 flex justify-center space-x-2">
          <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
          <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
          <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
        </div>
      </div>

      {/* Construction cone decoration */}
      <div className="absolute bottom-24 left-10 text-6xl opacity-65 hidden md:block animate-pulse" style={{animationDelay: '0.25'}}>
        🚧
      </div>
      <div className="absolute bottom-24 right-10 text-6xl opacity-65 hidden md:block animate-pulse" style={{animationDelay: '0.50'}}>
        🚧
      </div>
    </div>
  );
};

// ============================================
// END MAINTENANCE MODE CONFIGURATION
// ============================================
// ============================================
// GENERAL SITE CONFIGURATION
// ============================================

  // IMAGES - Put your image URLs here
  const IMAGES = {
    logo: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/AW-Logo_USA.png',
    heroBg: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/power-washing-results.gif',
  // Team member photos
  team: {
    wayne: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/Wayne.png', // Founder/CEO: Wayne Surles
    angie: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/Angie.png', // Co-Founder: Angie Surles
    justin: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/Justin.png' // Field Supervisor/Lead Technician: Justin Smith
  },
  // Before/After Gallery Images
  gallery: [
    {
      before: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/Before-After/01-Before.jpg',
      after: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/Before-After/01-After.jpg',
      description: 'Vinyl Siding Transformation'
    },
    {
      before: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/Before-After/02-Before.jpg',
      after: 'https://r2.fivemanage.com/NFGS8AhEJAk5KA8TseWa1/AWPressureWash/Before-After/02-After.jpg',
      description: 'Metal Carport Restoration'
    }
    // {
    //   before: 'https://images.unsplash.com/photo-1588854337221-4cf9fa96059f?w=800&q=80',
    //   after: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&q=80',
    //   description: 'Deck Cleaning & Restoration'
    // },
    // {
    //   before: 'https://images.unsplash.com/photo-1588854337221-4cf9fa96059f?w=800&q=80',
    //   after: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&q=80',
    //   description: 'Commercial Property Cleaning'
    // }
  ]
};
// BUSINESS INFO
  const BUSINESS = {
    name: 'A&W Pressure Washing',
    tagline: 'Veteran Owned • 45 Years Experience',
    phone: '(931) 259-7963',
    email: 'sales@awpressurewash.com',
    serviceArea: 'Middle Tennessee',
    serviceAreaDetailed: 'Warren, Dekalb, and surrounding counties',
    yearFounded: '1980',
    hoursOfOperation: 'Mon-Sat: 8:00 AM - 6:00 PM'
};
// SOCIAL MEDIA LINKS
const SOCIAL = {
  facebook:  'https://www.facebook.com/AWPressureWash',
  instagram: 'https://www.instagram.com/awpressurepros',
  yelp:      'https://yelp.awpressurewash.com',
  reddit:    'https://reddit.awpressurewash.com',
  tiktok:    'https://tiktok.awpressurewash.com',
  youtube:   'https://youtube.awpressurewash.com',
  discord:   'https://discord.awpressurewash.com',
};
// ============================================
// END CONFIGURATION - Don't edit below unless you know what you're doing
// ============================================



// Main App Component with Routing + Maintenance Check
const PressureWashingWebsite = () => {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/legal" element={<LegalPage />} />
    </Routes>
  );
};

// Handles the maintenance-mode gate for the homepage only
const HomeRoute = () => {
  const params = new URLSearchParams(window.location.search);
  const bypassMaintenance = params.get('admin') === ADMIN_SECRET;

  if (MAINTENANCE_MODE && !bypassMaintenance) {
    return <MaintenancePage />;
  }

  return <NormalSite />;
};

// Normal Site Component (all your existing code)
const NormalSite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollProgress, setScrollProgress] = useState(0);

  // Handle scroll progress for header effects
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.pageYOffset / totalScroll) * 100;
      setScrollProgress(currentProgress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mount the Cal.com floating "Free Estimate" button once for the whole site
  useEffect(() => {
    mountFloatingButton();
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Progress Bar */}
      <div className="fixed top-0 right-50 w-full h-3 bg-white/10 z-50">
        <div 
          className="h-full bg-cyan-500/75 transition-all duration-400 rounded"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Header/Navigation */}
      <Header 
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={scrollToSection}
        activeSection={activeSection}
      />

      {/* Hero Section */}
      <HeroSection scrollToSection={scrollToSection} />

      {/* About Section */}
      <AboutSection />

      {/* Team Section */}
      <TeamSection />

      {/* Services Section */}
      <ServicesSection />

      {/* Gallery Section */}
      <GallerySection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Service Area Map Section */}
      <ServiceAreaMapSection />

      {/* Scheduling Section */}
      <SchedulingSection />

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
};

// Header Component with Navigation
const Header = ({ isMenuOpen, setIsMenuOpen, scrollToSection, activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'team', label: 'Our Team' },
    { id: 'services', label: 'Services' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'servicearea', label: 'Service Area' },
    { id: 'scheduling', label: 'Book Now' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed w-full top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/85' : 'bg-white/100'}`}>
      <nav className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            {IMAGES.logo ? (
              <img 
                src={IMAGES.logo} 
                alt={`${BUSINESS.name} Logo`}
                className="h-16 w-auto object-contain"
              />
            ) : (
              <Droplet className="w-8 h-8 text-blue-600" />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">{BUSINESS.name}</h1>
              <p className="text-xs text-gray-600">{BUSINESS.tagline}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                  activeSection === item.id ? 'text-blue-600' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors">
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

// Hero Section with Animated Background
const HeroSection = ({ scrollToSection }) => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background with Image */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-green-600"
        style={{
          backgroundImage: `linear-gradient(rgba(66, 66, 66, 0.85), rgba(87, 87, 87, 0.40)), url(${IMAGES.heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full animate-pulse"
              style={{
                width: Math.random() * 100 + 50 + 'px',
                height: Math.random() * 100 + 50 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                animationDelay: Math.random() * 2 + 's',
                animationDuration: Math.random() * 3 + 2 + 's'
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center text-white">
        {/* Large Centered Logo (if provided) */}
        {IMAGES.logo ? (
          <div className="-mb-16">
            <img 
              src={IMAGES.logo} 
              alt={`${BUSINESS.name} Logo`}
              className="w-auto mx-auto hover:animate-bounce object-contain drop-shadow-teal-50"
              style={{ height: '450px' }}
            />
          </div>
        ) : (
          <h1 className="text-4xl md:text-7xl font-bold mb-6 animate-fade-in">
            We don't cut corners,<br />We clean them!
          </h1>
        )}
        
        <p className="text-xl md:text-2xl mb-20 max-w-2xl mx-auto font-extrabold text-cyan-300">
          Professional pressure washing services for Middle Tennessee homes and businesses. 
          45 years of trusted, eco-friendly cleaning solutions.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => scrollToSection('services')}
            className="bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-700 transform hover:scale-105 transition-all shadow-lg"
          >
            Our Services
          </button>
        </div>

        {/* Trust Badges */}
        <div className="mt-10 flex flex-wrap justify-center gap-8 text-xl text-red-600">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-6 h-6" />
            <span className="font-bold">Veteran Owned</span>
          </div>
          <div className="flex items-center space-x-2 text-white">
            <CheckCircle className="w-6 h-6" />
            <span className="font-bold">45+ Years Experience</span>
          </div>
          <div className="flex items-center space-x-2 text-white">
            <CheckCircle className="w-6 h-6" />
            <span className="font-bold">Eco-Friendly</span>
          </div>
          <div className="flex items-center space-x-2 text-blue-600">
            <CheckCircle className="w-6 h-6" />
            <span className="font-bold">Free Estimates</span>
          </div>
        </div>
      </div>

      {/* Tagline at bottom edge of green section (only shows if logo is used) */}
      {IMAGES.logo && (
        <div className="absolute bottom-4 left-0 right-0 bg-gradient-to-t from-transparent py-4 z-10">
          <p className="font-style: italic text-cyan-700 text-xl md:text-xl font-semibold text-center tracking-wide">
            We don't cut corners, We clean them!
          </p>
        </div>
      )}
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-gray-200">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About A & W</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Family Values, Professional Results
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                For over 45 years, A & W Pressure Washing and Cleaning has been Middle Tennessee's 
                trusted choice for professional cleaning services. As a proud veteran-owned and 
                family-operated business, we bring military precision and family values to every job.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Our commitment to excellence extends beyond just cleaning surfaces. We use 
                environmentally friendly products that are safe for your family, pets, and the 
                planet, while delivering results that speak for themselves.
              </p>
              <p className="text-lg text-gray-700">
                Whether it's your home's vinyl siding, concrete driveway, or commercial property, 
                we treat every project with the care and attention it deserves. That's the A & W difference.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">45+</div>
                <div className="text-gray-700">Years of Experience</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
                <div className="text-gray-700">Eco-Friendly</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">5000+</div>
                <div className="text-gray-700">Happy Clients</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">FREE</div>
                <div className="text-gray-700">Estimates</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const team = [
    {
      name: 'Wayne Surles',
      role: 'Founder / CEO',
      image: IMAGES.team.wayne || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    },
    {
      name: 'Angie Surles',
      role: 'Co-Founder',
      image: IMAGES.team.angie || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    },
    {
      name: 'Justin Smith',
      role: 'Field Supervisor / Lead Technician',
      image: IMAGES.team.justin || 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    }
  ];

  return (
    <section id="team" className="bg-cyan-800 py-20 sm:py-24">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 lg:px-8 xl:grid-cols-3">
        <div className="max-w-xl">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6">
            Meet Our Team
          </h2>
          <p className="text-lg text-gray-400">
            We're a dynamic group of individuals who are passionate about what we do and dedicated to delivering the best results for our clients.
          </p>
        </div>
        <ul role="list" className="grid gap-x-8 gap-y-12 sm:grid-cols-2 sm:gap-y-16 xl:col-span-2">
          {team.map((member, index) => (
            <li key={index}>
              <div className="flex items-center gap-x-6">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-16 h-16 rounded-full outline outline-1 -outline-offset-1 outline-white/10 object-cover"
                />
                <div>
                  <h3 className="text-base font-semibold tracking-tight text-white">
                    {member.name}
                  </h3>
                  <p className="text-sm font-semibold text-blue-400">
                    {member.role}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// Services Section with Interactive Cards
const ServicesSection = () => {
  const services = [
    {
      icon: <Home className="w-12 h-12" />,
      title: "Residential Washing",
      description: "Transform your home's exterior with our gentle yet effective pressure washing. Perfect for vinyl siding, brick, and stucco.",
      features: ["Vinyl Siding", "Driveways", "Patios & Decks", "Fences", "Garage Floors", "Playground Equipment"]
    },
    {
      icon: <Building className="w-12 h-12" />,
      title: "Commercial Cleaning",
      description: "Keep your business looking professional with our commercial pressure washing services. We handle properties of all sizes.",
      features: ["Storefronts", "Parking Lots", "Warehouses", "Multi-Unit Buildings", "Shopping Carts", "Playground Equipment", "Drive-Thrus", "Trash Compactors"]
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Concrete Cleaning",
      description: "Remove years of dirt, oil stains, and grime from concrete surfaces. Restore your driveway and walkways to like-new condition.",
      features: ["Driveways", "Sidewalks", "Garage Floors", "Pool Decks"]
    },
    {
      icon: <Wind className="w-12 h-12" />,
      title: "Gutter Cleaning",
      description: "Protect your home from water damage with professional gutter cleaning. We remove debris and ensure proper water flow.",
      features: ["Debris Removal", "Downspout Flushing", "Inspection"]
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive pressure washing solutions tailored to your needs
          </p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Individual Service Card Component
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="bg-gradient-to-br from-blue-300 to-green-50 p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={`text-blue-600 mb-4 transform transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}>
        {service.icon}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
      <p className="text-gray-700 mb-4">{service.description}</p>
      <ul className="space-y-2">
        {service.features.map((feature, idx) => (
          <li key={idx} className="flex items-center text-gray-600">
            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Gallery Section with Before/After Slider
const GallerySection = () => {
  const [activeImage, setActiveImage] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);

  const nextImage = () => {
    setActiveImage((prev) => (prev + 1) % IMAGES.gallery.length);
    setSliderPosition(50);
  };

  const prevImage = () => {
    setActiveImage((prev) => (prev - 1 + IMAGES.gallery.length) % IMAGES.gallery.length);
    setSliderPosition(50);
  };

  return (
    <section id="gallery" className="py-20 bg-gray-200">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Work</h2>
          <p className="text-xl text-gray-600">See the dramatic difference our services make</p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Before/After Slider */}
            <div className="relative h-96">
              {/* Before Image */}
              <div 
                className="absolute inset-0 bg-gray-600"
                style={{
                  backgroundImage: `url(${IMAGES.gallery[activeImage].before})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded text-lg font-bold">
                  BEFORE
                </div>
              </div>

              {/* After Image with Slider */}
              <div
                className="absolute inset-0 bg-blue-600"
                style={{ 
                  backgroundImage: `url(${IMAGES.gallery[activeImage].after})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
                }}
              >
                <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded text-lg font-bold">
                  AFTER
                </div>
              </div>

              {/* Slider Handle */}
              <input
                type="range"
                min="0"
                max="100"
                value={sliderPosition}
                onChange={(e) => setSliderPosition(e.target.value)}
                className="absolute top-0 left-0 w-full h-full opacity-0 cursor-ew-resize z-10"
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-white z-20 pointer-events-none"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <ChevronLeft className="w-4 h-4 text-gray-700" />
                  <ChevronRight className="w-4 h-4 text-gray-700" />
                </div>
              </div>
            </div>

            {/* Image Description */}
            <div className="p-6 bg-white">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {IMAGES.gallery[activeImage].description}
              </h3>
              <p className="text-gray-600">Drag the slider to see the transformation</p>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors z-30"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors z-30"
            >
              <ChevronRight className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          {/* Thumbnail Navigation */}
          <div className="flex justify-center gap-4 mt-8">
            {IMAGES.gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveImage(index);
                  setSliderPosition(50);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  activeImage === index ? 'bg-blue-600 w-8' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Sarah Johnson",
      location: "Morrison, TN",
      rating: 5,
      text: "A & W did an amazing job on our home! The vinyl siding looks brand new. Professional, punctual, and reasonably priced. Highly recommend!",
      service: "Residential Washing"
    },
    {
      name: "Mike Patterson",
      location: "McMinnville, TN",
      rating: 5,
      text: "We've used A & W for our commercial property for 3 years now. Always reliable and thorough. The veteran-owned aspect means a lot to us too.",
      service: "Commercial Cleaning"
    },
    {
      name: "Jennifer Martinez",
      location: "Sparta, TN",
      rating: 5,
      text: "Our concrete driveway had oil stains we thought were permanent. A & W made it look like the day it was poured! Incredible results.",
      service: "Concrete Cleaning"
    },
    {
      name: "Robert Chen",
      location: "Smithville, TN",
      rating: 5,
      text: "Great experience from start to finish. They were careful around our landscaping and the eco-friendly products give us peace of mind with our kids and pets.",
      service: "Gutter Cleaning"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-blue-600 to-green-600 text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
          <p className="text-xl opacity-90">Don't just take our word for it</p>
          <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 md:p-12 shadow-2xl">
            <div className="flex justify-center mb-6">
              {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>

            <p className="text-2xl md:text-3xl text-center mb-8 italic">
              "{testimonials[activeTestimonial].text}"
            </p>

            <div className="text-center">
              <p className="font-bold text-xl">{testimonials[activeTestimonial].name}</p>
              <p className="opacity-90">{testimonials[activeTestimonial].location}</p>
              <p className="text-sm mt-2 opacity-75">{testimonials[activeTestimonial].service}</p>
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  activeTestimonial === index ? 'bg-white w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// // Service Area Map Section
// const ServiceAreaMapSection = () => {
//   return (
//     <section id="servicearea" className="py-20 bg-white">
//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Service Area</h2>
//           <p className="text-xl text-gray-600 max-w-2xl mx-auto">
//             Serving {BUSINESS.serviceArea} with professional pressure washing and exterior cleaning
//           </p>
//           <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
//         </div>

//         {/* Map wrapped in same-style container as the booking section */}
// <div className="w-full max-w-[1600px] mx-auto bg-gradient-to-br from-green-300 to-cyan-400 rounded-xl shadow-2xl p-6 md:p-10 mb-16">

//   <div
//     style={{
//       position: "relative",
//       width: "100%",
//       paddingBottom: "55%",   // slightly smaller height
//       height: 0,
//       overflow: "hidden",
//       borderRadius: "12px",
//     }}
//   >
//     <iframe
//       src="https://www.google.com/maps/d/embed?mid=1FjkJkA8-Y9cLKBp99lthL9CneQzyfIA&ehbc=2E312F"
//       style={{
//         position: "absolute",
//         top: 0,
//         left: 0,
//         width: "100%",
//         height: "100%",
//         border: "0",
//       }}
//       allowFullScreen=""
//       loading="lazy"
//       title="A & W Pressure Washing Service Area Map"
//     ></iframe>
//   </div>

// </div>


//         {/* Service Area Info Cards */}
//         <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
//           <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
//             <MapPin className="w-8 h-8 text-blue-600 mx-auto mb-3" />
//             <h3 className="text-lg font-bold text-gray-900 mb-2">Primary Service Area</h3>
//             <p className="text-gray-700">{BUSINESS.serviceAreaDetailed}</p>
//           </div>
//           <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
//             <Phone className="w-8 h-8 text-green-600 mx-auto mb-3" />
//             <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to Help</h3>
//             <p className="text-gray-700">{BUSINESS.hoursOfOperation}</p>
//             <p className="text-blue-600 font-semibold mt-2">{BUSINESS.phone}</p>
//           </div>
//           <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
//             <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" />
//             <h3 className="text-lg font-bold text-gray-900 mb-2">Expanded Service Areas</h3>
//             <p className="text-gray-700">We also serve neighboring counties by special request</p>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// Service Area Map Section
const ServiceAreaMapSection = () => {
  // Desktop only: map ignores the mouse wheel until clicked, so page scrolling never gets trapped
  const [isMapActive, setIsMapActive] = useState(false);

  return (
    <section id="servicearea" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Service Area</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Serving {BUSINESS.serviceArea} with professional pressure washing and exterior cleaning
          </p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>

        {/* Map card: capped width, centered horizontally */}
        <div className="w-full max-w-5xl mx-auto bg-gradient-to-br from-green-300 to-cyan-400 rounded-xl shadow-2xl p-4 md:p-6 mb-16">
          <div
            onMouseLeave={() => setIsMapActive(false)}
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '56%',
              height: 0,
              overflow: 'hidden',
              borderRadius: '12px',
            }}
          >
            <iframe
              src="https://www.google.com/maps/d/embed?mid=1FjkJkA8-Y9cLKBp99lthL9CneQzyfIA&ehbc=2E312F"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: '0',
              }}
              allowFullScreen=""
              loading="lazy"
              title="A & W Pressure Washing Service Area Map"
            ></iframe>

            {/* Desktop-only scroll shield; disappears after a click, returns on mouse leave */}
            {!isMapActive && (
              <button
                type="button"
                onClick={() => setIsMapActive(true)}
                aria-label="Click to interact with the service area map"
                className="hidden md:flex absolute inset-0 w-full h-full items-center justify-center bg-transparent hover:bg-black/10 transition-colors cursor-pointer"
                style={{ zIndex: 10 }}
              >
                <span className="bg-black/70 text-white text-sm font-semibold px-4 py-2 rounded-full opacity-0 hover:opacity-100 transition-opacity">
                  Click to interact with map
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Service Area Info Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
            <MapPin className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Primary Service Area</h3>
            <p className="text-gray-700">{BUSINESS.serviceAreaDetailed}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
            <Phone className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to Help</h3>
            <p className="text-gray-700">{BUSINESS.hoursOfOperation}</p>
            <p className="text-blue-600 font-semibold mt-2">{BUSINESS.phone}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg shadow-lg text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Expanded Service Areas</h3>
            <p className="text-gray-700">We also serve neighboring counties by special request</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Scheduling Section — Cal.com inline embed
const SchedulingSection = () => {
  const didMount = useRef(false);

  useEffect(() => {
    // Ref guard prevents a duplicate iframe under React StrictMode's double-invoked effects
    if (didMount.current) return;
    didMount.current = true;
    mountInlineEmbed('#my-cal-inline-residentialestimate');
  }, []);

  return (
    <section id="scheduling" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Schedule Your Service
          </h2>
          <p className="text-xl text-gray-600">Book your free estimate today</p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>

        {/* Booking card: same width, gradient border, padding, and corner radius as the map card */}
        <div className="w-full max-w-5xl mx-auto bg-gradient-to-br from-green-300 to-cyan-400 rounded-xl shadow-2xl p-4 md:p-6">
          <div className="bg-white rounded-xl overflow-hidden">
            <div
              id="my-cal-inline-residentialestimate"
              style={{ width: '100%', minHeight: '650px', overflow: 'auto' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!contactForm.name.trim()) newErrors.name = 'Name is required';
    if (!contactForm.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(contactForm.email)) newErrors.email = 'Email is invalid';
    if (!contactForm.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {

        const response = await fetch(WORKER_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'contact',
            formData: {
              name: contactForm.name,
              email: contactForm.email,
              phone: contactForm.phone,
              message: contactForm.message,
            }
          })
        });

        if (response.ok) {
          setIsSubmitted(true);
          setTimeout(() => {
            setIsSubmitted(false);
            setContactForm({ 
              name: '', 
              email: '', 
              phone: '', 
              message: '' 
            });
          }, 5000);
        } else {
          alert('Error sending message. Please try again.');
        }
      } catch (error) {
        console.error('Contact error:', error);
        alert('Error sending message. Please call us directly at (931) 259-7963');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-200">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-600">We'd love to hear from you</p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-200 p-3 rounded-lg">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                  <p className="text-gray-600">{BUSINESS.phone}</p>
                  <p className="text-sm text-gray-500">{BUSINESS.hoursOfOperation}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-blue-200 p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-600">{BUSINESS.email}</p>
                  <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-blue-200 p-3 rounded-lg">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Service Area</h4>
                  <p className="text-gray-600">{BUSINESS.serviceArea}</p>
                  <p className="text-sm text-gray-500">{BUSINESS.serviceAreaDetailed}</p>
                </div>
              </div>
            </div>

            {/* Why Choose Us Box */}
            <div className="mt-8 bg-gradient-to-br from-blue-600 to-green-600 text-white p-6 rounded-xl">
              <h4 className="text-xl font-bold mb-4">Why Choose A & W?</h4>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  45+ years of trusted service
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Veteran & family owned
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Eco-friendly products
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Free estimates
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-b from-green-500 to-blue-600 rounded-xl shadow-2xl p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600">Thank you for contacting us. We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-600`}
                    placeholder="Your name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-600`}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="(615) 555-0123"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleChange}
                    rows="5"
                    className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-600`}
                    placeholder="How can we help you?"
                  ></textarea>
                  {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  className="w-full bg-cyan-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ scrollToSection }) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  return (
    <footer className="bg-gray-900 text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              {IMAGES.logo ? (
                <img 
                  src={IMAGES.logo} 
                  alt={`${BUSINESS.name} Logo`}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <Droplet className="w-8 h-8 text-blue-400" />
              )}
              <div>
                <h3 className="font-bold text-lg">{BUSINESS.name}</h3>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Professional pressure washing services for {BUSINESS.serviceArea}. Veteran-owned with 45 years of experience.
            </p>
          <div className="flex space-x-4">
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>

            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>

            <a href={SOCIAL.yelp} target="_blank" rel="noopener noreferrer" aria-label="Yelp" className="text-gray-400 hover:text-red-500 transition-colors">
              <FaYelp className="w-6 h-6" />
            </a>

            <a href={SOCIAL.reddit} target="_blank" rel="noopener noreferrer" aria-label="Reddit" className="text-gray-400 hover:text-orange-500 transition-colors">
              <FaReddit className="w-6 h-6" />
            </a>

            <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-gray-400 hover:text-white transition-colors">
              <FaTiktok className="w-6 h-6" />
            </a>

            <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-gray-400 hover:text-red-600 transition-colors">
              <FaYoutube className="w-6 h-6" />
            </a>

            <a href={SOCIAL.discord} target="_blank" rel="noopener noreferrer" aria-label="Discord" className="text-gray-400 hover:text-indigo-400 transition-colors">
              <FaDiscord className="w-6 h-6" />
            </a>
          </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white transition-colors">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-white transition-colors">
                  Gallery
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('servicearea')} className="text-gray-400 hover:text-white transition-colors">
                  Service Area
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigate('/legal')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Policies
                </button>
              </li>
            </ul>
          </div>
          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-4">Our Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Residential Washing</li>
              <li>Commercial Cleaning</li>
              <li>Concrete Cleaning</li>
              <li>Gutter Cleaning</li>
              <li>Vinyl Siding</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{BUSINESS.phone}</span>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>{BUSINESS.email}</span>
              </li>
              <li className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{BUSINESS.serviceAreaDetailed}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6 text-center text-gray-500 text-sm">
          <p>&copy;{currentYear} {BUSINESS.name} All rights reserved.</p>
          <p className="mt-2">Veteran-Owned | Family-Operated & Serving {BUSINESS.serviceArea} Since {BUSINESS.yearFounded}</p>
        </div>
      </div>
    </footer>
  );
};

export default PressureWashingWebsite;
